import React, { useEffect, useCallback, useState } from 'react';
import { Plus, Play, Edit2, BarChart3, Archive, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Spinner } from '../ui/Spinner';
import { WorkflowDefinition } from '../../types/workflows';
import { apiClient } from '../../lib/api';

interface WorkflowsListProps {
  datasetId: string;
  onCreateNew: () => void;
  onEditWorkflow: (workflow: WorkflowDefinition) => void;
  onViewRuns: (workflow: WorkflowDefinition) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const WorkflowsList: React.FC<WorkflowsListProps> = ({
  datasetId,
  onCreateNew,
  onEditWorkflow,
  onViewRuns,
  isLoading,
  setIsLoading
}) => {
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadWorkflows = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient.getWorkflows();
      
      // Filter workflows for this dataset (if needed)
      // For now, showing all user workflows
      const userWorkflows = response.workflows || [];
      setWorkflows(userWorkflows);
    } catch (err: any) {
      console.error('Error loading workflows:', err);
      setError('Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    loadWorkflows();
  }, [datasetId, loadWorkflows]);

  const handleExecuteWorkflow = async (workflowId: string) => {
    try {
      await apiClient.executeWorkflow(workflowId);
      // Refresh workflows to update status
      loadWorkflows();
    } catch (err: any) {
      console.error('Error executing workflow:', err);
      setError('Failed to execute workflow');
    }
  };

  const handlePublishWorkflow = async (workflowId: string) => {
    try {
      await apiClient.publishWorkflow(workflowId);
      // Refresh workflows to update status
      loadWorkflows();
    } catch (err: any) {
      console.error('Error publishing workflow:', err);
      setError('Failed to publish workflow');
    }
  };

  const WORKFLOW_STATUS_CONFIG = {
    PUBLISHED: {
      icon: <CheckCircle className="w-4 h-4 text-green-500" />,
      color: 'success' as const
    },
    DRAFT: {
      icon: <Clock className="w-4 h-4 text-yellow-500" />,
      color: 'warning' as const
    },
    ARCHIVED: {
      icon: <Archive className="w-4 h-4 text-gray-500" />,
      color: 'primary-light' as const
    },
    default: {
      icon: <AlertTriangle className="w-4 h-4 text-gray-400" />,
      color: 'primary-light' as const
    }
  };

  const getStatusIcon = (status: string) => {
    const statusKey = status as keyof typeof WORKFLOW_STATUS_CONFIG;
    return WORKFLOW_STATUS_CONFIG[statusKey]?.icon || WORKFLOW_STATUS_CONFIG.default.icon;
  };

  const getStatusColor = (status: string) => {
    const statusKey = status as keyof typeof WORKFLOW_STATUS_CONFIG;
    return WORKFLOW_STATUS_CONFIG[statusKey]?.color || WORKFLOW_STATUS_CONFIG.default.color;
  };

  if (isLoading) {
    return <Spinner center />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Workflow Automations</h3>
          <p className="text-sm text-gray-600 mt-1">
            Create and manage automated workflows for your dataset
          </p>
        </div>
        <Button onClick={onCreateNew} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Create Workflow</span>
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Workflows List */}
      {workflows.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No workflows yet</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create your first workflow automation to streamline your lead generation and outreach process.
          </p>
          <Button onClick={onCreateNew}>
            Create Your First Workflow
          </Button>
        </div>
      ) : (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pb-16">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-base font-medium text-gray-900">
                      {workflow.name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(workflow.status || 'DRAFT')}
                      <Badge variant={getStatusColor(workflow.status || 'DRAFT')}>
                        {workflow.status || 'DRAFT'}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    {workflow.description}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>
                      {workflow.updated_at 
                        ? `Last Updated - ${new Date(workflow.updated_at).toLocaleDateString()}`
                        : `Created - ${workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'Recently'}`
                      }
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {workflow.status === 'PUBLISHED' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleExecuteWorkflow(workflow.id!)}
                      className="flex items-center space-x-1"
                    >
                      <Play className="w-3 h-3" />
                      <span>Run</span>
                    </Button>
                  )}

                  {workflow.status === 'DRAFT' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handlePublishWorkflow(workflow.id!)}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                    >
                      Publish
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onViewRuns(workflow)}
                    className="flex items-center space-x-1"
                  >
                    <BarChart3 className="w-3 h-3" />
                    <span>Runs</span>
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onEditWorkflow(workflow)}
                    className="flex items-center space-x-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    <span>Edit</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};